import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { clientsApi } from '@/api';
import useClientStore from '@/stores/useClientStore';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import EntrepriseSearch from '@/components/ui/EntrepriseSearch';
import { FORME_JURIDIQUE_OPTIONS } from '@/constants/enums';

const TYPE_OPTIONS = [
  { value: 'particulier', label: 'Particulier' },
  { value: 'entreprise', label: 'Entreprise' },
];

const CIVILITE_OPTIONS = [
  { value: 'M.', label: 'Monsieur' },
  { value: 'Mme', label: 'Madame' },
  { value: 'Société', label: 'Société' },
];

export default function ClientFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { createClient, updateClient } = useClientStore();

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    type: 'particulier',
    civilite: 'M.',
    nom: '',
    prenom: '',
    raison_sociale: '',
    qualite: '',
    adresse: '',
    code_postal: '',
    commune: '',
    telephone: '',
    email: '',
    siret: '',
    forme_juridique: '',
    representant_nom: '',
    representant_prenom: '',
    representant_fonction: '',
  });

  // Fetch existing client for edit mode
  useEffect(() => {
    if (!isEdit) return;

    const fetchClient = async () => {
      try {
        const client = await clientsApi.get(id);
        setForm({
          type: client.type || 'particulier',
          civilite: client.civilite || 'M.',
          nom: client.nom || '',
          prenom: client.prenom || '',
          raison_sociale: client.raison_sociale || '',
          qualite: client.qualite || '',
          adresse: client.adresse || '',
          code_postal: client.code_postal || '',
          commune: client.commune || '',
          telephone: client.telephone || '',
          email: client.email || '',
          siret: client.siret || '',
          forme_juridique: client.forme_juridique || '',
          representant_nom: client.representant_nom || '',
          representant_prenom: client.representant_prenom || '',
          representant_fonction: client.representant_fonction || '',
        });
      } catch (_err) {
        setError('Client introuvable.');
      } finally {
        setFetchLoading(false);
      }
    };
    fetchClient();
  }, [id, isEdit]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEntrepriseSelect = (entreprise) => {
    setForm((prev) => ({
      ...prev,
      raison_sociale: entreprise.nom_complet || prev.raison_sociale,
      siret: entreprise.siret || prev.siret,
      adresse: entreprise.adresse || prev.adresse,
      code_postal: entreprise.code_postal || prev.code_postal,
      commune: entreprise.commune || prev.commune,
      representant_nom: entreprise.dirigeant
        ? entreprise.dirigeant.split(' ').slice(1).join(' ')
        : prev.representant_nom,
      representant_prenom: entreprise.dirigeant
        ? entreprise.dirigeant.split(' ')[0]
        : prev.representant_prenom,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Build payload — omit empty optional fields
    const payload = {
      type: form.type,
      civilite: form.civilite,
      nom: form.nom,
      prenom: form.prenom || undefined,
      raison_sociale: form.raison_sociale || undefined,
      qualite: form.qualite || undefined,
      adresse: form.adresse || undefined,
      code_postal: form.code_postal || undefined,
      commune: form.commune || undefined,
      telephone: form.telephone || undefined,
      email: form.email || undefined,
      siret: form.siret || undefined,
      forme_juridique: form.forme_juridique || undefined,
      representant_nom: form.representant_nom || undefined,
      representant_prenom: form.representant_prenom || undefined,
      representant_fonction: form.representant_fonction || undefined,
    };

    try {
      if (isEdit) {
        await updateClient(id, payload);
      } else {
        await createClient(payload);
      }
      navigate('/clients');
    } catch (err) {
      setError(err.message || 'Une erreur est survenue.');
      setLoading(false);
    }
  };

  const isValid = () => {
    if (!form.nom.trim()) return false;
    if (form.type === 'particulier' && !form.prenom.trim()) return false;
    if (form.type === 'entreprise' && !form.raison_sociale.trim()) return false;
    if (form.siret && !/^\d{14}$/.test(form.siret)) return false;
    return true;
  };

  if (fetchLoading) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <p className="text-slate-500">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/clients')}
          className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            {isEdit ? 'Modifier le client' : 'Nouveau client'}
          </h1>
          <p className="text-slate-600 mt-1">
            {isEdit
              ? 'Modifiez les informations du client'
              : 'Renseignez les informations du nouveau client'}
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card className="p-6 space-y-6">
          {/* Type de client */}
          <Select
            label="Type de client"
            name="type"
            options={TYPE_OPTIONS}
            value={form.type}
            onChange={(e) => handleChange('type', e.target.value)}
          />

          {/* Civilité */}
          <Select
            label="Civilité"
            name="civilite"
            options={CIVILITE_OPTIONS}
            value={form.civilite}
            onChange={(e) => handleChange('civilite', e.target.value)}
          />

          {/* Nom */}
          <Input
            label="Nom"
            placeholder="Dupont"
            value={form.nom}
            onChange={(e) => handleChange('nom', e.target.value)}
            required
          />

          {/* Champs particulier */}
          {form.type === 'particulier' && (
            <Input
              label="Prénom"
              placeholder="Jean"
              value={form.prenom}
              onChange={(e) => handleChange('prenom', e.target.value)}
              required
            />
          )}

          {/* Champs entreprise */}
          {form.type === 'entreprise' && (
            <div className="space-y-4">
              <EntrepriseSearch
                label="Rechercher une entreprise (nom ou SIRET)"
                name="entrepriseSearch"
                onSelect={handleEntrepriseSelect}
              />

              <Input
                label="Raison sociale"
                placeholder="SARL Dupont Immobilier"
                value={form.raison_sociale}
                onChange={(e) => handleChange('raison_sociale', e.target.value)}
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="SIRET"
                  placeholder="12345678901234"
                  value={form.siret}
                  onChange={(e) => handleChange('siret', e.target.value)}
                />
                <Select
                  label="Forme juridique"
                  name="forme_juridique"
                  options={[{ value: '', label: '— Sélectionner —' }, ...FORME_JURIDIQUE_OPTIONS]}
                  value={form.forme_juridique}
                  onChange={(e) => handleChange('forme_juridique', e.target.value)}
                />
              </div>

              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Représentant</h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Nom"
                      placeholder="Dupont"
                      value={form.representant_nom}
                      onChange={(e) => handleChange('representant_nom', e.target.value)}
                    />
                    <Input
                      label="Prénom"
                      placeholder="Jean"
                      value={form.representant_prenom}
                      onChange={(e) => handleChange('representant_prenom', e.target.value)}
                    />
                  </div>
                  <Input
                    label="Fonction"
                    placeholder="Gérant, Directeur, Président..."
                    value={form.representant_fonction}
                    onChange={(e) => handleChange('representant_fonction', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Qualité */}
          <Input
            label="Qualité"
            placeholder="Propriétaire, acquéreur, banque, notaire..."
            value={form.qualite}
            onChange={(e) => handleChange('qualite', e.target.value)}
          />

          {/* Coordonnées */}
          <div className="border-t pt-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Coordonnées</h2>
            <div className="space-y-4">
              <Input
                label="Email"
                type="email"
                placeholder="jean.dupont@example.com"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
              />
              <Input
                label="Téléphone"
                placeholder="06 12 34 56 78"
                value={form.telephone}
                onChange={(e) => handleChange('telephone', e.target.value)}
              />
            </div>
          </div>

          {/* Adresse */}
          <div className="border-t pt-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Adresse</h2>
            <div className="space-y-4">
              <Input
                label="Adresse"
                placeholder="12 rue de la Paix"
                value={form.adresse}
                onChange={(e) => handleChange('adresse', e.target.value)}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Code postal"
                  placeholder="75001"
                  value={form.code_postal}
                  onChange={(e) => handleChange('code_postal', e.target.value)}
                />
                <Input
                  label="Commune"
                  placeholder="Paris"
                  value={form.commune}
                  onChange={(e) => handleChange('commune', e.target.value)}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 mt-6">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/clients')}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!isValid() || loading}
          >
            <Save className="w-4 h-4" />
            {loading
              ? 'Enregistrement...'
              : isEdit
                ? 'Enregistrer'
                : 'Créer le client'}
          </Button>
        </div>
      </form>
    </div>
  );
}
